import {useTranslation} from '@aboutyou/i18n';
import {RouterLinkTarget} from '@aboutyou/router';
import {styled} from '@linaria/react';
import React, {CSSProperties, ComponentProps, RefObject} from 'react';
import {COLORS} from '../../../constants/colors';
import {useMatchesMediaQuery} from '../../../hooks/useMatchesMediaQuery/useMatchesMediaQuery';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {ProductImage, ProductImageProps} from '../ProductImage/ProductImage';
import {HoverImage, ProductTileHover} from '../ProductTile/ProductTileHover';
import {
  GoToSlideWrapper,
  ProductTileImageSlider,
  StyledSliderBullets,
} from '../ProductTileImageSlider/ProductTileImageSlider';

export type Image = {
  alt?: string;
  padded?: boolean;
  src: string;
};

export type ProductTileImageProps = {
  productImageContent?: React.ReactNode;
  columns?: ProductImageProps['columns'];
  breakpoints?: ProductImageProps['breakpoints'];
  importance?: 'high' | 'low' | 'auto';
  onClick?: (ev: React.MouseEvent) => void;
  primaryImage: Image;
  secondaryImage?: Image;
  style?: CSSProperties;
  to?: RouterLinkTarget;
  enableAddCTA?: boolean;
  replaceHistory?: boolean;
  onLoad?: VoidFunction;
  imageGallery?: Image[];
  trackGallerySwipe?: VoidFunction;
  productTileGalleryMode?: 'hover' | 'always';
  enableImageGalleryAnimation?: boolean;
  openInNewTab?: boolean;
};

export const ProductTileImage = ({
  productImageContent = [],
  children,
  columns,
  breakpoints,
  importance,
  primaryImage,
  secondaryImage,
  to = '#',
  onClick,
  replaceHistory,
  onLoad,
  productTileGalleryMode,
  imageGallery,
  trackGallerySwipe,
  enableImageGalleryAnimation,
  openInNewTab,
  ref,
  ...rest
}: React.PropsWithChildren<ProductTileImageProps> & {
  ref?: React.Ref<HTMLAnchorElement>;
}) => {
  const {t} = useTranslation();
  const supportsHover = useMatchesMediaQuery('(pointer: fine)');

  const productTileImageSliderProps: ComponentProps<
    typeof ProductTileImageSlider
  > = {
    productImageContent,
    columns,
    breakpoints,
    importance,
    onLoad,
    imageGallery: imageGallery || [],
    productTileGalleryMode,
    trackGallerySwipe,
    enableImageGalleryAnimation,
  };

  const staticImageProps: ComponentProps<typeof StaticImage> = {
    productImageContent,
    columns,
    breakpoints,
    importance,
    primaryImage,
    secondaryImage,
    onLoad,
    ref,
  };

  return (
    <Wrapper
      data-testid="productImageWrapper"
      ref={ref}
      onClick={onClick}
      to={to}
      replace={replaceHistory}
      forcePushBehaviour={replaceHistory}
      data-gallery-mode-hover={productTileGalleryMode === 'hover'}
      aria-label={t('Go to the article detail page')}
      openLinkInNewTab={openInNewTab}
      {...rest}
    >
      {(productTileGalleryMode === 'always' ||
        (supportsHover && productTileGalleryMode === 'hover')) &&
      imageGallery &&
      imageGallery.length > 1 ? (
        <ProductTileImageSlider {...productTileImageSliderProps} />
      ) : (
        <StaticImage {...staticImageProps} />
      )}

      {children}
    </Wrapper>
  );
};

export const StaticImage = ({
  productImageContent = [],
  children,
  columns,
  breakpoints,
  importance,
  primaryImage,
  secondaryImage,
  onLoad,
  ref,
}: React.PropsWithChildren<ProductTileImageProps> & {
  ref?: React.Ref<HTMLAnchorElement>;
}) => {
  return (
    <>
      <ProductImage
        alt={primaryImage.alt}
        columns={columns}
        breakpoints={breakpoints}
        importance={importance}
        loading={importance === 'high' ? 'eager' : undefined}
        padded={primaryImage.padded}
        src={primaryImage.src}
        onLoad={onLoad}
        increasePadding
      >
        {productImageContent}
      </ProductImage>
      {secondaryImage &&
        secondaryImage.src !== primaryImage.src &&
        process.env.STACK === 'desktop' && (
          <ProductTileHover
            alt={secondaryImage.alt}
            hoverRef={ref as RefObject<HTMLAnchorElement | null>}
            padded={secondaryImage.padded}
            src={secondaryImage.src}
          />
        )}
      {children}
    </>
  );
};

export const Wrapper = styled(UnstyledLink)`
  color: inherit;
  background: ${COLORS.White};
  cursor: pointer;
  display: block;
  overflow: hidden;
  position: relative;
  z-index: 0;
  text-decoration: none;
  height: 100%;

  &[data-gallery-mode-hover='true'] {
    ${StyledSliderBullets} {
      opacity: 0;
    }
    &:hover ${StyledSliderBullets} {
      opacity: 1;
      transform: translateY(-65px);

      display: flex;
    }
    &:hover ${GoToSlideWrapper} {
      transform: translateX(0);
    }
  }

  @media (pointer: fine) {
    &:hover {
      ${HoverImage} {
        opacity: 1;
        transform: none;
        transition-delay: 150ms;
      }
    }
  }
`;
